/**
 * Sanction — Plausible Theme-Level Tracking
 *
 * All theme-side Plausible events in one file. Uses DOM observation
 * and event delegation — no inline injections into stock theme files.
 *
 * Loaded via: <script src="{{ 'plausible-tracking.js' | asset_url }}" defer></script>
 *
 * Events handled:
 *   - Search (enriched) — quick search + results page
 *   - Article Read (75% scroll)
 *   - Newsletter Signup (success element detection)
 *   - Size Guide Opened (click delegation)
 *   - Collection Viewed (on /collections/* pageload)
 *
 * Events handled in their own custom snippets (not here):
 *   - Truck Finder Used — in snippets/product-truck-size-finder.liquid
 *   - Chat Used — in assets/product-chat-v2.js
 *
 * Preorder tracking: no dedicated event — the pixel's Purchase event
 * already includes has_preorder=yes/no via hasPreorderProperty().
 * Filter Purchase by has_preorder=yes for completed-preorder counts.
 *
 * Session storage bridge keys (read by the Custom Pixel at checkout):
 *   - sanction_used_search, sanction_last_search_query
 *   - sanction_used_size_guide
 *   - sanction_used_truck_finder (written by truck finder snippet)
 *   - sanction_used_chat (written by product-chat-v2.js)
 */
(function() {
  'use strict';
  if (!window.plausible) return;

  // ── Helpers ──────────────────────────────────────────────────────
  function ss(key, val) {
    try { sessionStorage.setItem(key, val); } catch(e) {}
  }

  // ── Search (enriched: quick search) ─────────────────────────────
  // Debounced 600ms + min 3 chars to filter out keystroke fragments.
  // One event per settled query, not one per character typed.
  var searchEl = document.querySelector('main-search');
  if (searchEl) {
    var lastSearchQuery = '';
    var searchTimer = null;

    new MutationObserver(function() {
      var hasResults = searchEl.classList.contains('main-search--has-results');
      var noResults = searchEl.classList.contains('main-search--no-results');
      if (!hasResults && !noResults) return;

      clearTimeout(searchTimer);
      searchTimer = setTimeout(function() {
        var input = searchEl.querySelector('.main-search__input');
        var query = (input && input.value || '').toLowerCase().trim().slice(0, 60);
        if (!query || query.length < 3 || query === lastSearchQuery) return;
        lastSearchQuery = query;

        var nowHasResults = searchEl.classList.contains('main-search--has-results');
        var results = searchEl.querySelectorAll('.main-search-result');
        var count = results.length;

        plausible('Search', {
          props: {
            query: query,
            results_count: count,
            has_results: nowHasResults ? 'yes' : 'no',
            surface: 'quick_search'
          }
        });
        ss('sanction_used_search', 'yes');
        ss('sanction_last_search_query', query);
      }, 600);
    }).observe(searchEl, { attributes: true, attributeFilter: ['class'] });
  }

  // ── Search (enriched: full results page) ────────────────────────
  // Detects /search?q= pages and reads results from the DOM.
  if (window.location.pathname.indexOf('/search') !== -1) {
    var params = new URLSearchParams(window.location.search);
    var searchTerms = (params.get('q') || '').toLowerCase().trim().slice(0, 60);
    if (searchTerms) {
      var resultBlocks = document.querySelectorAll('.product-grid .product-block');
      var noResultsEl = document.querySelector('.align-centre.fully-spaced-row');
      var count = noResultsEl ? 0 : resultBlocks.length;

      plausible('Search', {
        props: {
          query: searchTerms,
          results_count: count,
          has_results: count > 0 ? 'yes' : 'no',
          surface: 'results_page'
        }
      });
      ss('sanction_used_search', 'yes');
      ss('sanction_last_search_query', searchTerms);
    }
  }

  // ── Article Read (75% scroll) ───────────────────────────────────
  // Gated on /blogs/<blog>/<article> URL + .article--main wrapper
  // (sections/main-article.liquid). Other pages share .rte.cf / h1
  // classes, so we need both guards to avoid firing everywhere.
  if (/^\/blogs\/[^/]+\/[^/]+/.test(window.location.pathname)) {
    var articleWrapper = document.querySelector('.article--main');
    var articleBody = articleWrapper && articleWrapper.querySelector('.rte');
    var articleTitle = document.querySelector('.text-overlay__title, .majortitle');
    if (articleBody && articleTitle) {
      var articleFired = false;

      var checkArticleScroll = function() {
        if (articleFired) return;
        var rect = articleBody.getBoundingClientRect();
        var scrolled = window.innerHeight - rect.top;
        if (rect.height > 0 && scrolled / rect.height >= 0.75) {
          articleFired = true;

          plausible('Article Read', {
            props: {
              title: (articleTitle.textContent || '').trim().slice(0, 80)
            }
          });
          window.removeEventListener('scroll', checkArticleScroll);
        }
      };

      window.addEventListener('scroll', checkArticleScroll, { passive: true });
    }
  }

  // ── Newsletter Signup ───────────────────────────────────────────
  // Detects the success element that Liquid renders on form post.
  // Observes the DOM for the success div appearing (handles both
  // initial page load and AJAX re-render).
  var signupForms = document.querySelectorAll('.signup-form');
  signupForms.forEach(function(form) {
    var successEl = form.querySelector('.signup-form__response--success');
    if (successEl) {
      plausible('Newsletter Signup', {
        props: { location: form.closest('[id]')?.id || 'unknown' }
      });
      return;
    }

    new MutationObserver(function(mutations, observer) {
      var success = form.querySelector('.signup-form__response--success');
      if (success) {
        plausible('Newsletter Signup', {
          props: { location: form.closest('[id]')?.id || 'unknown' }
        });
        observer.disconnect();
      }
    }).observe(form, { childList: true, subtree: true });
  });

  // ── Size Guide Opened ──────────────────────────────────────────
  // Product-specs tabs use data-tab="sizing" on the tab button.
  document.addEventListener('click', function(evt) {
    if (!evt.target.closest('[data-tab="sizing"]')) return;

    var typeEl = document.querySelector('[data-product-type]');
    var productType = (typeEl && typeEl.dataset.productType) ||
                      (window.location.pathname.match(/\/products\//) ? 'unknown' : '');
    if (!productType) return;

    plausible('Size Guide Opened', {
      props: { product_type: productType }
    });
    ss('sanction_used_size_guide', 'yes');
  }, true);

  // ── Collection Viewed ──────────────────────────────────────────
  // Fires once on any /collections/* page load. Captures handle + the
  // on-page result count so we can spot collections with thin results.
  // Vendor-search URLs like /collections/vendors?q=burton carry the
  // brand in the ?q= param — include it as filter_query.
  if (window.location.pathname.indexOf('/collections/') === 0) {
    var collMatch = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    var collHandle = collMatch ? collMatch[1] : '';
    if (collHandle) {
      var collBlocks = document.querySelectorAll('.product-grid .product-block');
      var collParams = new URLSearchParams(window.location.search);
      var filterQuery = (collParams.get('q') || '').toLowerCase().trim().slice(0, 60);

      plausible('Collection Viewed', {
        props: {
          collection_handle: collHandle,
          product_count: collBlocks.length,
          filter_query: filterQuery || 'none'
        }
      });
    }
  }

})();
